// AM Vocab — admin page for v2 AM detection vocabulary.
//
// The bot scores each caller transcript against this list (each phrase has a
// weight). Cumulative score across the call; when it hits the avatar's
// am_v2_threshold, the bot hangs up as 'answering-machine'. Per-avatar opt-in
// (avatar.am_v2_enabled) controls whether v2 runs for that avatar at all.
//
// "Extract from phone numbers" lets you paste known AM phone numbers; the
// backend pulls their transcripts and shows candidate phrases to import as
// vocab entries.
import { useEffect, useState } from 'react'
import { motion } from 'framer-motion'
import { Plus, Trash2, Edit3, Save, X, Power, Voicemail, RefreshCw, Search } from 'lucide-react'
import { API_BASE_URL } from '../services/api'

const EMPTY_ROW = { phrase: '', weight: 3, enabled: 1, notes: '' }

async function api(path, opts = {}) {
  const r = await fetch(`${API_BASE_URL}${path}`, {
    headers: { 'Content-Type': 'application/json' },
    ...opts,
    body: opts.body ? JSON.stringify(opts.body) : undefined,
  })
  const data = await r.json()
  if (!r.ok || data.success === false) throw new Error(data.error || `HTTP ${r.status}`)
  return data
}

export default function AmVocab() {
  const [vocab, setVocab] = useState([])
  const [catches, setCatches] = useState([])
  const [loading, setLoading] = useState(true)
  const [err, setErr] = useState(null)
  const [editingId, setEditingId] = useState(null)
  const [draft, setDraft] = useState(EMPTY_ROW)
  const [extractInput, setExtractInput] = useState('')
  const [extractRes, setExtractRes] = useState(null)
  const [extracting, setExtracting] = useState(false)

  const load = async () => {
    setLoading(true)
    setErr(null)
    try {
      const [v, c] = await Promise.all([api('/am-v2/vocab'), api('/am-v2/catches?limit=50')])
      const list = (v.vocab || []).slice().sort((a, b) => (b.weight - a.weight) || a.phrase.localeCompare(b.phrase))
      setVocab(list)
      setCatches(c.catches || [])
    } catch (e) {
      setErr(e.message)
    } finally {
      setLoading(false)
    }
  }
  useEffect(() => { load() }, [])

  const startNew = () => { setEditingId('new'); setDraft({ ...EMPTY_ROW }) }
  const startEdit = (r) => { setEditingId(r.id); setDraft({ ...r, enabled: r.enabled ? 1 : 0 }) }
  const cancel = () => { setEditingId(null); setDraft(EMPTY_ROW) }
  const save = async () => {
    if (!draft.phrase?.trim()) { alert('phrase required'); return }
    try {
      if (editingId === 'new') {
        await api('/am-v2/vocab', { method: 'POST', body: { phrase: draft.phrase.trim(), weight: draft.weight, notes: draft.notes } })
      } else {
        await api(`/am-v2/vocab/${editingId}`, { method: 'PUT', body: { phrase: draft.phrase.trim(), weight: draft.weight, enabled: draft.enabled, notes: draft.notes } })
      }
      cancel()
      load()
    } catch (e) { alert('save failed: ' + e.message) }
  }
  const toggle = async (row) => {
    try { await api(`/am-v2/vocab/${row.id}`, { method: 'PUT', body: { enabled: row.enabled ? 0 : 1 } }); load() }
    catch (e) { alert(e.message) }
  }
  const remove = async (row) => {
    if (!confirm(`Delete "${row.phrase}"?`)) return
    try { await api(`/am-v2/vocab/${row.id}`, { method: 'DELETE' }); load() }
    catch (e) { alert(e.message) }
  }

  const runExtract = async () => {
    const nums = extractInput.split(/[\s,;]+/).map(s => s.replace(/\D/g, '')).filter(s => s.length >= 7)
    if (nums.length === 0) { alert('Paste at least one phone number'); return }
    setExtracting(true)
    try {
      const r = await api('/am-v2/extract', { method: 'POST', body: { phone_numbers: nums } })
      setExtractRes(r)
    } catch (e) { alert(e.message) }
    finally { setExtracting(false) }
  }
  const importCandidate = async (c) => {
    try {
      await api('/am-v2/vocab', { method: 'POST', body: { phrase: c.phrase, weight: c.suggested_weight } })
      load()
      // mark imported in extract panel
      if (extractRes) {
        setExtractRes({
          ...extractRes,
          candidates: extractRes.candidates.map(x => x.phrase === c.phrase ? { ...x, already_in_vocab: true } : x),
        })
      }
    } catch (e) { alert(e.message) }
  }

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} style={{ padding: 24 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18 }}>
        <h2 style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Voicemail size={22} /> AM Vocab (v2 detection)
        </h2>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-secondary" onClick={load}><RefreshCw size={14} /> Reload</button>
          <button className="btn btn-primary" onClick={startNew}><Plus size={14} /> Add phrase</button>
        </div>
      </div>

      {err && <div style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', padding: 10, borderRadius: 8, marginBottom: 12 }}>{err}</div>}

      {/* Extract from phone numbers */}
      <div style={{ background: 'rgba(139,92,246,0.06)', border: '1px solid rgba(139,92,246,0.25)', borderRadius: 8, padding: 14, marginBottom: 18 }}>
        <h3 style={{ margin: '0 0 6px 0', fontSize: 14, display: 'flex', alignItems: 'center', gap: 6 }}>
          <Search size={14} /> Extract candidate phrases from known AM phone numbers
        </h3>
        <p style={{ margin: '0 0 10px 0', fontSize: 12, opacity: 0.75 }}>
          Paste phone numbers of calls you know are AMs (one per line or comma-separated). We'll pull each call's transcript and show phrases ranked by how many numbers used them — strong cross-phone matches are good vocab candidates.
        </p>
        <textarea
          className="input"
          rows={4}
          placeholder="9544470321&#10;2149797375&#10;6143851975"
          value={extractInput}
          onChange={(e) => setExtractInput(e.target.value)}
          style={{ width: '100%', fontFamily: 'monospace', fontSize: 12 }}
        />
        <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
          <button className="btn btn-primary" onClick={runExtract} disabled={extracting}>
            {extracting ? 'Extracting…' : <><Search size={14} /> Extract</>}
          </button>
          {extractRes && (
            <button className="btn btn-secondary" onClick={() => { setExtractRes(null); setExtractInput('') }}><X size={14} /> Clear</button>
          )}
        </div>
        {extractRes && (
          <div style={{ marginTop: 10 }}>
            <div style={{ fontSize: 11, opacity: 0.7, marginBottom: 6 }}>
              Scanned {extractRes.scanned_phones} numbers, found {extractRes.found_calls} calls. {extractRes.note || ''}
            </div>
            <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: 'rgba(255,255,255,0.04)' }}>
                  <th style={{ textAlign: 'left', padding: 6 }}>Phrase</th>
                  <th style={{ padding: 6 }}>Phones</th>
                  <th style={{ padding: 6 }}>Calls</th>
                  <th style={{ padding: 6 }}>Suggested weight</th>
                  <th style={{ padding: 6 }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {(extractRes.candidates || []).slice(0, 50).map((c, i) => (
                  <tr key={i} style={{ borderTop: '1px solid rgba(255,255,255,0.05)' }}>
                    <td style={{ padding: 6, fontFamily: 'monospace' }}>{c.phrase}</td>
                    <td style={{ padding: 6, textAlign: 'center' }}>{c.unique_phones}</td>
                    <td style={{ padding: 6, textAlign: 'center' }}>{c.unique_calls}</td>
                    <td style={{ padding: 6, textAlign: 'center' }}>{c.suggested_weight}</td>
                    <td style={{ padding: 6, textAlign: 'center' }}>
                      {c.already_in_vocab
                        ? <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>in vocab</span>
                        : <button className="btn btn-sm btn-primary" onClick={() => importCandidate(c)}><Plus size={12} /> Import</button>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Vocab list */}
      <h3 style={{ fontSize: 14, marginBottom: 8 }}>Vocabulary ({vocab.length})</h3>
      <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse' }}>
        <thead>
          <tr style={{ background: 'rgba(255,255,255,0.04)' }}>
            <th style={{ textAlign: 'left', padding: 8 }}>Phrase</th>
            <th style={{ padding: 8, width: 90 }}>Weight</th>
            <th style={{ padding: 8, width: 70 }}>Enabled</th>
            <th style={{ padding: 8, width: 70 }}>Catches</th>
            <th style={{ textAlign: 'left', padding: 8 }}>Notes</th>
            <th style={{ padding: 8, width: 130 }}></th>
          </tr>
        </thead>
        <tbody>
          {editingId === 'new' && (
            <tr style={{ background: 'rgba(34,197,94,0.06)' }}>
              <td style={{ padding: 6 }}><input className="input" value={draft.phrase} onChange={e => setDraft({ ...draft, phrase: e.target.value })} placeholder="e.g. leave a message" /></td>
              <td style={{ padding: 6 }}><input className="input" type="number" min="1" max="20" value={draft.weight} onChange={e => setDraft({ ...draft, weight: parseInt(e.target.value) || 3 })} /></td>
              <td style={{ padding: 6, textAlign: 'center' }}>—</td>
              <td style={{ padding: 6, textAlign: 'center' }}>—</td>
              <td style={{ padding: 6 }}><input className="input" value={draft.notes} onChange={e => setDraft({ ...draft, notes: e.target.value })} placeholder="optional" /></td>
              <td style={{ padding: 6 }}>
                <button className="btn btn-sm btn-primary" onClick={save}><Save size={12} /> Save</button>
                <button className="btn btn-sm btn-secondary" onClick={cancel} style={{ marginLeft: 4 }}><X size={12} /></button>
              </td>
            </tr>
          )}
          {vocab.map(r => editingId === r.id ? (
            <tr key={r.id} style={{ background: 'rgba(139,92,246,0.06)' }}>
              <td style={{ padding: 6 }}><input className="input" value={draft.phrase} onChange={e => setDraft({ ...draft, phrase: e.target.value })} /></td>
              <td style={{ padding: 6 }}><input className="input" type="number" min="1" max="20" value={draft.weight} onChange={e => setDraft({ ...draft, weight: parseInt(e.target.value) || 3 })} /></td>
              <td style={{ padding: 6, textAlign: 'center' }}>
                <input type="checkbox" checked={!!draft.enabled} onChange={e => setDraft({ ...draft, enabled: e.target.checked ? 1 : 0 })} />
              </td>
              <td style={{ padding: 6, textAlign: 'center' }}>{r.catches || 0}</td>
              <td style={{ padding: 6 }}><input className="input" value={draft.notes || ''} onChange={e => setDraft({ ...draft, notes: e.target.value })} /></td>
              <td style={{ padding: 6 }}>
                <button className="btn btn-sm btn-primary" onClick={save}><Save size={12} /></button>
                <button className="btn btn-sm btn-secondary" onClick={cancel} style={{ marginLeft: 4 }}><X size={12} /></button>
              </td>
            </tr>
          ) : (
            <tr key={r.id} style={{ borderTop: '1px solid rgba(255,255,255,0.05)', opacity: r.enabled ? 1 : 0.5 }}>
              <td style={{ padding: 8, fontFamily: 'monospace' }}>{r.phrase}</td>
              <td style={{ padding: 8, textAlign: 'center', fontWeight: 600 }}>{r.weight}</td>
              <td style={{ padding: 8, textAlign: 'center' }}>{r.enabled ? '✓' : '—'}</td>
              <td style={{ padding: 8, textAlign: 'center' }}>{r.catches || 0}</td>
              <td style={{ padding: 8, fontSize: 11, opacity: 0.7 }}>{r.notes || ''}</td>
              <td style={{ padding: 8, textAlign: 'right' }}>
                <button className="btn btn-sm btn-ghost" onClick={() => toggle(r)} title={r.enabled ? 'Disable' : 'Enable'}><Power size={12} /></button>
                <button className="btn btn-sm btn-ghost" onClick={() => startEdit(r)}><Edit3 size={12} /></button>
                <button className="btn btn-sm btn-ghost danger" onClick={() => remove(r)}><Trash2 size={12} /></button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Recent catches */}
      <h3 style={{ fontSize: 14, margin: '24px 0 8px' }}>Recent v2 catches ({catches.length})</h3>
      <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse' }}>
        <thead>
          <tr style={{ background: 'rgba(255,255,255,0.04)' }}>
            <th style={{ textAlign: 'left', padding: 8 }}>Phone</th>
            <th style={{ textAlign: 'left', padding: 8 }}>Bot</th>
            <th style={{ textAlign: 'left', padding: 8 }}>Avatar</th>
            <th style={{ padding: 8 }}>Score</th>
            <th style={{ textAlign: 'left', padding: 8 }}>Matched phrases</th>
            <th style={{ textAlign: 'left', padding: 8 }}>When</th>
          </tr>
        </thead>
        <tbody>
          {catches.length === 0 && (
            <tr><td colSpan={6} style={{ padding: 14, textAlign: 'center', opacity: 0.6 }}>No catches yet. Enable AM v2 on an avatar and wait for a bot call.</td></tr>
          )}
          {catches.map(c => (
            <tr key={c.id} style={{ borderTop: '1px solid rgba(255,255,255,0.05)' }}>
              <td style={{ padding: 8, fontFamily: 'monospace' }}>{c.phone_number || <em style={{ opacity: 0.5 }}>—</em>}</td>
              <td style={{ padding: 8 }}>{c.sip_user || ''}</td>
              <td style={{ padding: 8 }}>{c.avatar_name || c.avatar_id || ''}</td>
              <td style={{ padding: 8, textAlign: 'center', fontWeight: 600 }}>{c.total_score}</td>
              <td style={{ padding: 8, fontSize: 11, opacity: 0.85 }}>{(c.matched || []).map(m => `${m.phrase}(+${m.weight})`).join(', ')}</td>
              <td style={{ padding: 8, fontSize: 11, opacity: 0.7 }}>{new Date(c.created_at).toLocaleString()}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </motion.div>
  )
}
